// Which earlier step a data-dependent step is missing, if any. Anchored on data
// emptiness, never on step status: a step with partial data shows its normal
// surface, so a PM who skips ahead on purpose is never nagged. "Empty" mirrors
// each lambda's own precondition (synthesis refuses without dialog extractions,
// capabilities without synthesised needs, the deck without an idea; alignment
// runs on an empty graph but has nothing to compare without one), so the hint
// never appears where Generate would have worked.

import type { StepId } from '@/features/project/step-status';

export interface PrerequisiteInputs {
  hasIdea: boolean;
  // Raw dialog extractions: nodes with metadata.source === 'dialog'.
  dialogExtractionCount: number;
  marketSignalCount: number;
  assumptionCount: number;
  // Synthesised challenges and opportunities (metadata.source === 'synthesis').
  // Synthesised jobs are context, not needs, so they don't count.
  synthesisedNeedCount: number;
  capabilityCount: number;
}

export type HintedStepId = 'synthesis' | 'capabilities' | 'thesis' | 'deck';

interface Feeder {
  step: StepId;
  count: (inputs: PrerequisiteInputs) => number;
}

// Feeders in journey order. The hint fires only when every feeder is empty, and
// then points at the first one: the earliest step that would give this one
// something to work with. Adding a hinted step is one row.
const FEEDERS: Record<HintedStepId, { needsIdea: boolean; feeders: Feeder[] }> = {
  synthesis: {
    needsIdea: false,
    feeders: [{ step: 'dialog', count: (i) => i.dialogExtractionCount }],
  },
  capabilities: {
    needsIdea: false,
    feeders: [{ step: 'synthesis', count: (i) => i.synthesisedNeedCount }],
  },
  thesis: {
    needsIdea: true,
    feeders: [
      { step: 'assumptions', count: (i) => i.assumptionCount },
      { step: 'synthesis', count: (i) => i.synthesisedNeedCount },
      { step: 'capabilities', count: (i) => i.capabilityCount },
    ],
  },
  deck: {
    needsIdea: true,
    feeders: [
      { step: 'market', count: (i) => i.marketSignalCount },
      { step: 'assumptions', count: (i) => i.assumptionCount },
      { step: 'synthesis', count: (i) => i.synthesisedNeedCount },
      { step: 'capabilities', count: (i) => i.capabilityCount },
    ],
  },
};

export function missingPrerequisite(stepId: HintedStepId, inputs: PrerequisiteInputs): StepId | null {
  const { needsIdea, feeders } = FEEDERS[stepId];
  if (needsIdea && !inputs.hasIdea) return 'idea';
  if (feeders.every((f) => f.count(inputs) === 0)) return feeders[0].step;
  return null;
}

// The node shape useStepStatuses maps canvas nodes to (type + metadata.source),
// plus the title: the synthesis and capabilities lambdas only accept inputs
// with a non-blank title, and the hint must agree with them or a PM could
// blank a title in the node modal, lose the hint, and still get the 400.
export interface PrerequisiteNode {
  type?: string;
  metadataSource?: string;
  title?: string;
}

const hasTitle = (n: PrerequisiteNode) => typeof n.title === 'string' && n.title.trim().length > 0;
const isNeedType = (type?: string) => type === 'challenge' || type === 'opportunity';

export function derivePrerequisiteInputs(nodes: PrerequisiteNode[], hasIdea: boolean): PrerequisiteInputs {
  let dialogExtractionCount = 0;
  let marketSignalCount = 0;
  let assumptionCount = 0;
  let synthesisedNeedCount = 0;
  let capabilityCount = 0;
  for (const n of nodes) {
    // Mirrors ai-synthesis: a dialog-sourced job, challenge or opportunity with a title.
    if (n.metadataSource === 'dialog' && (n.type === 'job' || isNeedType(n.type)) && hasTitle(n)) {
      dialogExtractionCount++;
    }
    if (n.type === 'market_signal') marketSignalCount++;
    if (n.type === 'assumption') assumptionCount++;
    if (n.type === 'capability') capabilityCount++;
    // Mirrors ai-capabilities: a synthesised challenge or opportunity with a title.
    if (n.metadataSource === 'synthesis' && isNeedType(n.type) && hasTitle(n)) synthesisedNeedCount++;
  }
  return { hasIdea, dialogExtractionCount, marketSignalCount, assumptionCount, synthesisedNeedCount, capabilityCount };
}
